import React, { useState, useEffect } from 'react';
import { 
  Wallet, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, 
  Search, Filter, RefreshCw, PlusCircle, MinusCircle, ShieldAlert, 
  Gift, CreditCard, Sparkles, Download, User, ArrowRight, DollarSign,
  TrendingUp, Award, History, FileText, Check, ChevronRight, Coins
} from 'lucide-react';
import { walletsAPI } from '../utils/api';
import { FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';

const WalletManagement = () => {
  const [activeTab, setActiveTab] = useState('wallets'); // 'wallets' or 'ledger'
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    total_balance: 0, 
    total_diamonds: 0,
    total_pending_diamonds: 0,
    total_wallets: 0, 
    total_added: 0,
    total_gift_spent: 0,
    total_recharges: 0
  });
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, GIFT, RECHARGE, DEDUCT, REWARD, BONUS
  const [currencyFilter, setCurrencyFilter] = useState('ALL'); // ALL, COIN, DIAMOND
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [selectedUserFilter, setSelectedUserFilter] = useState('');

  // User History Modal State
  const [historyModalUser, setHistoryModalUser] = useState(null);
  const [modalTransactions, setModalTransactions] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Balance Adjust Modal States
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [adjustType, setAdjustType] = useState('ADD'); // 'ADD' or 'DEDUCT'
  const [adjustCurrency, setAdjustCurrency] = useState('COIN'); // 'COIN' or 'DIAMOND'
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  // Exchange Slabs state
  const [exchangeSlabs, setExchangeSlabs] = useState([]);
  const [customRatio, setCustomRatio] = useState(1.0);
  const [savingSlabs, setSavingSlabs] = useState(false);
  const [slabSaveSuccess, setSlabSaveSuccess] = useState(false);

  // Coin Slabs (INR -> Coins) state
  const [coinSlabs, setCoinSlabs] = useState([]);
  const [savingCoinSlabs, setSavingCoinSlabs] = useState(false);
  const [coinSlabSaveSuccess, setCoinSlabSaveSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, txStatsRes, walletsRes, txsRes, slabsRes, coinSlabsRes] = await Promise.all([
        walletsAPI.getWalletStats().catch(() => ({ data: { data: {} } })),
        walletsAPI.getTransactionStats().catch(() => ({ data: { data: [] } })),
        walletsAPI.getWallets().catch(() => ({ data: { data: [] } })),
        walletsAPI.getAllTransactions().catch(() => ({ data: { data: [] } })),
        walletsAPI.getExchangeSlabs().catch(() => ({ data: { data: { slabs: [], custom_ratio: 1.0 } } })),
        walletsAPI.getCoinSlabs().catch(() => ({ data: { data: [] } }))
      ]);
      
      const statsData = statsRes.data?.data || {};
      const txStatsData = txStatsRes.data?.data || [];
      const txList = txsRes.data?.data || [];
      const walletList = walletsRes.data?.data || [];
      const slabsData = slabsRes.data?.data?.slabs || [];
      const ratioData = slabsRes.data?.data?.custom_ratio ?? 1.0;
      const fetchedCoinSlabs = coinSlabsRes.data?.data || [];

      // Calculate total diamonds circulating
      const totalDiamonds = walletList.reduce((acc, curr) => acc + (Number(curr.diamond_balance) || 0), 0);
      
      let totalAdd = 0;
      let totalGift = 0;
      let totalRecharge = 0;

      txStatsData.forEach(item => {
        const type = (item.transaction_type || '').toUpperCase();
        const amt = Math.abs(Number(item.total_amount) || 0);
        if (type === 'COIN_ADD' || type === 'SELF_RECHARGE' || type === 'FRIEND_RECHARGE') {
          totalAdd += amt;
          if (type.includes('RECHARGE')) totalRecharge += amt;
        } else if (type === 'GIFT_SENT') {
          totalGift += amt;
        }
      });
      
      setStats({
        total_balance: Number(statsData.total_balance) || 0,
        total_diamonds: totalDiamonds,
        total_pending_diamonds: Number(statsData.total_pending_diamonds) || walletList.reduce((acc, curr) => acc + (Number(curr.total_pending_diamonds) || 0), 0),
        total_wallets: Number(statsData.total_wallets) || walletList.length || 0,
        total_added: totalAdd,
        total_gift_spent: totalGift,
        total_recharges: totalRecharge
      });

      setWallets(walletList);
      setTransactions(txList);
      setExchangeSlabs(slabsData.length > 0 ? slabsData : [
        { id: 1, diamond_amount: 10000, coin_amount: 20000, multiplier: 2.0, is_active: true, sort_order: 1 },
        { id: 2, diamond_amount: 50000, coin_amount: 100000, multiplier: 2.0, is_active: true, sort_order: 2 },
        { id: 3, diamond_amount: 100000, coin_amount: 200000, multiplier: 2.0, is_active: true, sort_order: 3 },
        { id: 4, diamond_amount: 500000, coin_amount: 1000000, multiplier: 2.0, is_active: true, sort_order: 4 },
        { id: 5, diamond_amount: 1000000, coin_amount: 2000000, multiplier: 2.0, is_active: true, sort_order: 5 },
        { id: 6, diamond_amount: 5000000, coin_amount: 10000000, multiplier: 2.0, is_active: true, sort_order: 6 }
      ]);
      setCustomRatio(ratioData);
      
      setCoinSlabs(fetchedCoinSlabs.length > 0 ? fetchedCoinSlabs : [
        { id: 1, coin_amount: 100, price_inr: 10, discount_pct: 0, is_active: true, sort_order: 1 },
        { id: 2, coin_amount: 500, price_inr: 50, discount_pct: 0, is_active: true, sort_order: 2 },
        { id: 3, coin_amount: 1000, price_inr: 100, discount_pct: 0, is_active: true, sort_order: 3 },
      ]);
    } catch (err) {
      console.error("Error loading wallet details:", err);
    } finally {
      setLoading(false);
    }
  };

// Number formatting helpers
const formatNumberWithCommas = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
  if (!numStr) return '';
  return Number(numStr).toLocaleString('en-IN');
};

const getRawNumber = (val) => {
  if (!val) return 0;
  const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
  return Number(numStr) || 0;
};

const formatAmountInWords = (num) => {
  const n = Number(num);
  if (!n || n <= 0) return '';
  if (n >= 10000000) {
    const cr = (n / 10000000).toFixed(2).replace(/\.00$/, '');
    return `${cr} Crore Coins (${n.toLocaleString('en-IN')})`;
  }
  if (n >= 100000) {
    const lk = (n / 100000).toFixed(2).replace(/\.00$/, '');
    return `${lk} Lakh Coins (${n.toLocaleString('en-IN')})`;
  }
  if (n >= 1000) {
    const th = (n / 1000).toFixed(2).replace(/\.00$/, '');
    return `${th} Thousand Coins (${n.toLocaleString('en-IN')})`;
  }
  return `${n.toLocaleString('en-IN')} Coins`;
};

  const handleOpenAdjustModal = (wallet, type, currency = 'COIN') => {
    setSelectedWallet(wallet);
    setAdjustType(type);
    setAdjustCurrency(currency);
    setAdjustAmount('');
    setAdjustReason('');
    setIsAdjustModalOpen(true);
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    const rawAmt = getRawNumber(adjustAmount);
    if (!selectedWallet || rawAmt <= 0) return;
    
    setAdjustLoading(true);
    try {
      const payload = {
        amount: rawAmt, // Raw clean integer sent to backend
        reason: adjustReason.trim() || `Admin manual adjustment (${adjustCurrency} ${adjustType})`
      };
      
      let res;
      if (adjustCurrency === 'DIAMOND') {
        if (adjustType === 'ADD') {
          res = await walletsAPI.addDiamonds(selectedWallet.user_id, payload);
        } else {
          res = await walletsAPI.deductDiamonds(selectedWallet.user_id, payload);
        }
      } else {
        if (adjustType === 'ADD') {
          res = await walletsAPI.addCoins(selectedWallet.user_id, payload);
        } else {
          res = await walletsAPI.deductCoins(selectedWallet.user_id, payload);
        }
      }
      
      if (res.data.success) {
        setIsAdjustModalOpen(false);
        fetchData();
      }
    } catch (err) {
      alert("Adjustment failed: " + (err.response?.data?.message || err.message));
    } finally {
      setAdjustLoading(false);
    }
  };

  // Open User-specific History Modal
  const handleViewUserHistory = async (wallet) => {
    setHistoryModalUser(wallet);
    setModalLoading(true);
    setModalTransactions([]);
    try {
      const targetId = wallet.user_id || wallet.id || wallet.xoxo_id;
      const [walletRes, txsRes] = await Promise.all([
        walletsAPI.getWallet(targetId).catch(() => null),
        walletsAPI.getTransactions(targetId).catch(() => null)
      ]);

      if (walletRes?.data?.data) {
        setHistoryModalUser({
          ...wallet,
          balance: Number(walletRes.data.data.balance || 0),
          diamond_balance: Number(walletRes.data.data.diamond_balance || 0)
        });
      }
      if (txsRes?.data?.data && Array.isArray(txsRes.data.data)) {
        setModalTransactions(txsRes.data.data);
      }
    } catch (e) {
      console.error("Error fetching live user wallet & txs:", e);
    } finally {
      setModalLoading(false);
    }
  };

  // Switch to Ledger filtered by this user
  const handleFilterLedgerByUser = async (xoxoId) => {
    setSelectedUserFilter(xoxoId);
    setActiveTab('ledger');
    setHistoryModalUser(null);
    try {
      const res = await walletsAPI.getAllTransactions({ userId: xoxoId, limit: 2000 });
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setTransactions(res.data.data);
      }
    } catch (e) {
      console.error("Error loading user ledger:", e);
    }
  };

  // Export transactions to CSV
  const exportToCSV = () => {
    if (transactions.length === 0) return;
    const headers = ["Txn ID", "Date", "User Name", "Email", "XOXO ID", "Currency", "Type", "Amount", "Balance Before", "Balance After", "Reason"];
    const rows = transactions.map(t => [
      t.id,
      new Date(t.created_at || t.timestamp).toISOString(),
      `"${t.user_name || ''}"`,
      t.email || '',
      t.xoxo_id || '',
      t.currency_type || 'COIN',
      t.transaction_type || '',
      t.amount || 0,
      t.balance_before || 0,
      t.balance_after || 0,
      `"${(t.reason || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `xoxo_financial_ledger_${new Date().toISOString().substring(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Slab handlers
  const handleSlabChange = (index, field, value) => {
    const updated = [...exchangeSlabs];
    const cleanVal = (field === 'diamond_amount' || field === 'coin_amount')
      ? getRawNumber(value)
      : value;
    updated[index] = {
      ...updated[index],
      [field]: cleanVal
    };
    // Auto update multiplier preview if diamond_amount or coin_amount changes
    if (field === 'diamond_amount' || field === 'coin_amount') {
      const d = Number(field === 'diamond_amount' ? cleanVal : updated[index].diamond_amount) || 0;
      const c = Number(field === 'coin_amount' ? cleanVal : updated[index].coin_amount) || 0;
      if (d > 0) {
        updated[index].multiplier = Number((c / d).toFixed(2));
      }
    }
    setExchangeSlabs(updated);
  };

  const handleCoinSlabChange = (index, field, value) => {
    const updated = [...coinSlabs];
    
    // Allow toggle of active state directly
    if (field === 'is_active') {
      updated[index][field] = value;
      setCoinSlabs(updated);
      return;
    }

    const cleanVal = String(value).replace(/[^0-9.]/g, '');
    updated[index][field] = cleanVal;

    // Auto-sync INR and USD prices
    const rate = 94.0;
    if (field === 'price_inr') {
      const inr = parseFloat(cleanVal);
      updated[index].price_usd = !isNaN(inr) && inr > 0 ? (inr / rate).toFixed(2) : '';
    } else if (field === 'price_usd') {
      const usd = parseFloat(cleanVal);
      updated[index].price_inr = !isNaN(usd) && usd > 0 ? Math.round(usd * rate) : '';
    }

    setCoinSlabs(updated);
  };

  const addNewCoinSlab = () => {
    setCoinSlabs([...coinSlabs, {
      coin_amount: 100, price_inr: 10, price_usd: '0.11', discount_pct: 0, is_active: true, sort_order: coinSlabs.length + 1
    }]);
  };

  const removeCoinSlab = (index) => {
    if (coinSlabs.length <= 1) return;
    const updated = [...coinSlabs];
    updated.splice(index, 1);
    // update sort_orders
    updated.forEach((s, i) => s.sort_order = i + 1);
    setCoinSlabs(updated);
  };

  const handleSaveCoinSlabs = async () => {
    setSavingCoinSlabs(true);
    try {
      const formattedSlabs = coinSlabs.map((s, i) => {
        const inr = Number(s.price_inr) || 0;
        const usd = Number(s.price_usd !== undefined && s.price_usd !== '' ? s.price_usd : (inr / 94.0).toFixed(2)) || 0;
        return {
          ...s,
          coin_amount: Number(s.coin_amount) || 0,
          price_inr: inr,
          price_usd: usd,
          discount_pct: Number(s.discount_pct) || 0,
          sort_order: i + 1
        };
      });

      const res = await walletsAPI.updateCoinSlabs({ slabs: formattedSlabs });
      if (res.data.success) {
        setCoinSlabSaveSuccess(true);
        if (res.data.data) {
          setCoinSlabs(res.data.data);
        }
        setTimeout(() => setCoinSlabSaveSuccess(false), 3000);
      }
    } catch (err) {
      alert("Failed to save coin slabs: " + (err.response?.data?.message || err.message));
    } finally {
      setSavingCoinSlabs(false);
    }
  };

  const handleSaveSlabs = async () => {
    setSavingSlabs(true);
    setSlabSaveSuccess(false);
    try {
      await walletsAPI.updateExchangeSlabs({
        slabs: exchangeSlabs,
        custom_ratio: customRatio
      });
      setSlabSaveSuccess(true);
      setTimeout(() => setSlabSaveSuccess(false), 4000);
      fetchData();
    } catch (err) {
      console.error("Error saving slabs:", err);
      alert("Failed to save exchange slabs: " + (err.response?.data?.message || err.message));
    } finally {
      setSavingSlabs(false);
    }
  };

  // Filters
  const filteredWallets = wallets.filter(w => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = !s ||
      (w.user_name && w.user_name.toLowerCase().includes(s)) ||
      (w.email && w.email.toLowerCase().includes(s)) ||
      (w.xoxo_id && String(w.xoxo_id).includes(s)) ||
      (w.country && w.country.toLowerCase().includes(s)) ||
      (w.user_id && w.user_id.toLowerCase().includes(s));
    const matchesCountry = countryFilter === 'ALL' || (w.country && w.country.toLowerCase() === countryFilter.toLowerCase());
    return matchesSearch && matchesCountry;
  });

  const filteredTransactions = transactions.filter(t => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = 
      (t.user_name && t.user_name.toLowerCase().includes(s)) ||
      (t.email && t.email.toLowerCase().includes(s)) ||
      (t.xoxo_id && String(t.xoxo_id).includes(s)) ||
      (t.transaction_type && t.transaction_type.toLowerCase().includes(s)) ||
      (t.reason && t.reason.toLowerCase().includes(s)) ||
      (t.id && t.id.toLowerCase().includes(s));

    if (!matchesSearch) return false;

    // Filter by selected specific user if set
    if (selectedUserFilter) {
      const matchUser = String(t.xoxo_id) === String(selectedUserFilter) || 
                        String(t.user_id) === String(selectedUserFilter);
      if (!matchUser) return false;
    }

    // Filter by Currency (Coins / Diamonds)
    if (currencyFilter !== 'ALL') {
      const curr = (t.currency_type || 'COIN').toUpperCase();
      if (curr !== currencyFilter) return false;
    }

    // Filter by Transaction Type
    if (typeFilter === 'ALL') return true;
    const type = (t.transaction_type || '').toUpperCase();
    if (typeFilter === 'GIFT') return type.includes('GIFT');
    if (typeFilter === 'RECHARGE') return type.includes('RECHARGE') || type === 'COIN_ADD';
    if (typeFilter === 'DEDUCT') return type.includes('DEDUCT');
    if (typeFilter === 'REWARD') return type.includes('REWARD') || type.includes('TARGET');
    if (typeFilter === 'BONUS') return type.includes('BONUS');
    return true;
  });

  // Get user-specific transactions for modal (prefer live fetched transactions)
  const userModalTransactions = modalTransactions.length > 0 
    ? modalTransactions 
    : (historyModalUser ? transactions.filter(t => 
        String(t.user_id) === String(historyModalUser.user_id) || 
        (historyModalUser.xoxo_id && String(t.xoxo_id) === String(historyModalUser.xoxo_id))
      ) : []);

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 p-8 rounded-[32px] text-white shadow-xl shadow-slate-900/10 border border-slate-700/50">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-pink-500/20 text-pink-400 rounded-2xl border border-pink-500/30">
              <DollarSign size={28} />
            </div>
            <div>
              <h2 className="text-3xl font-black tracking-tight">Financial Audit & System Ledger</h2>
              <p className="text-slate-300 text-sm mt-0.5">Har ek user ka 360° hisab-kitab — Coins, Diamonds, Target Rewards & Gifting history.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/15 text-white font-bold rounded-2xl transition active:scale-95 text-xs backdrop-blur-md"
          >
            <Download size={15} /> Export Ledger CSV
          </button>
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold rounded-2xl transition active:scale-95 shadow-lg shadow-pink-500/25 text-xs"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh Data
          </button>
        </div>
      </div>

      {/* KPI Cards / Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        
        {/* Total Circulating Coins */}
        <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-md transition">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-black uppercase text-pink-600 tracking-wider bg-pink-50 px-2.5 py-1 rounded-full border border-pink-100">
              Coin Balance
            </span>
            <div className="p-2.5 bg-pink-50 text-pink-600 rounded-2xl">
              <Wallet size={18} />
            </div>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Circulating Coins</p>
          <h3 className="text-2xl font-black text-slate-800 mt-1">
            🪙 {stats.total_balance.toLocaleString()}
          </h3>
          <p className="text-[11px] text-slate-400 font-semibold mt-2">Active across all user wallets</p>
        </div>

        {/* Total Circulating Diamonds */}
        <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-md transition">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-black uppercase text-purple-600 tracking-wider bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
              Available Diamonds
            </span>
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-2xl">
              <Sparkles size={18} />
            </div>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total User Diamonds</p>
          <h3 className="text-2xl font-black text-purple-700 mt-1">
            💎 {stats.total_diamonds.toLocaleString()}
          </h3>
          <p className="text-[11px] text-purple-500 font-semibold mt-2">Ready in wallet balance</p>
        </div>

        {/* Total Pending Diamonds (Awaiting 10 Days) */}
        <div className="bg-white p-5 rounded-3xl shadow-sm border border-amber-200/80 bg-amber-50/20 relative overflow-hidden group hover:shadow-md transition">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-black uppercase text-amber-700 tracking-wider bg-amber-100 px-2.5 py-1 rounded-full border border-amber-200">
              Pending Held
            </span>
            <div className="p-2.5 bg-amber-100 text-amber-700 rounded-2xl">
              <Clock size={18} />
            </div>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Held Pending Diamonds</p>
          <h3 className="text-2xl font-black text-amber-800 mt-1">
            ⏳ 💎 {(stats.total_pending_diamonds || 0).toLocaleString()}
          </h3>
          <p className="text-[11px] text-amber-600 font-semibold mt-2">Locked awaiting 10 streaming days</p>
        </div>

        {/* Total Gifts Sent */}
        <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-md transition">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-black uppercase text-rose-600 tracking-wider bg-rose-50 px-2.5 py-1 rounded-full border border-rose-100">
              Gifting Volume
            </span>
            <div className="p-2.5 bg-rose-50 text-rose-600 rounded-2xl">
              <Gift size={18} />
            </div>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Gifts Sent</p>
          <h3 className="text-2xl font-black text-rose-700 mt-1">
            {stats.total_gift_spent.toLocaleString()} <span className="text-xs font-semibold text-rose-400">Coins</span>
          </h3>
          <p className="text-[11px] text-rose-500 font-semibold mt-2">Coins spent in live streams</p>
        </div>

        {/* Total Active Wallets */}
        <div className="bg-white p-5 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-md transition">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-black uppercase text-blue-600 tracking-wider bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
              Holders
            </span>
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl">
              <User size={18} />
            </div>
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Active Wallets</p>
          <h3 className="text-2xl font-black text-slate-800 mt-1">
            {stats.total_wallets.toLocaleString()} <span className="text-xs font-semibold text-slate-400">Users</span>
          </h3>
          <p className="text-[11px] text-slate-400 font-semibold mt-2">Registered wallet accounts</p>
        </div>

      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-[32px] shadow-xl shadow-slate-100/50 border border-slate-100 overflow-hidden">
        
        {/* Toolbar & Filter Tabs */}
        <div className="p-6 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-slate-50/50">
          
          <div className="flex bg-slate-200/70 p-1 rounded-2xl border border-slate-200">
            <button 
              onClick={() => setActiveTab('wallets')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${activeTab === 'wallets' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <Wallet size={14} className={activeTab === 'wallets' ? 'text-pink-600' : ''} />
              User Wallets & History ({wallets.length})
            </button>
            <button 
              onClick={() => setActiveTab('ledger')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${activeTab === 'ledger' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <Clock size={14} className={activeTab === 'ledger' ? 'text-pink-600' : ''} />
              Full Financial Audit Ledger ({transactions.length})
            </button>
            <button 
              onClick={() => setActiveTab('slabs')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${activeTab === 'slabs' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <Sparkles size={14} className={activeTab === 'slabs' ? 'text-pink-600' : ''} />
              Exchange Slabs (💎 ➔ 🪙)
            </button>
            <button 
              onClick={() => setActiveTab('coinslabs')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all ${activeTab === 'coinslabs' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
            >
              <Coins size={14} className={activeTab === 'coinslabs' ? 'text-pink-600' : ''} />
              Coin Slabs (₹ ➔ 🪙)
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            {activeTab === 'ledger' && (
              <>
                {/* Active user filter badge if set */}
                {selectedUserFilter && (
                  <div className="flex items-center gap-1.5 bg-pink-100 text-pink-800 px-3 py-1.5 rounded-xl text-xs font-bold border border-pink-200">
                    <span>Filtering User: ID {selectedUserFilter}</span>
                    <button 
                      onClick={() => setSelectedUserFilter('')}
                      className="hover:text-pink-950 ml-1 p-0.5 rounded-full hover:bg-pink-200"
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                )}

                {/* Currency Filter (Coins vs Diamonds) */}
                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200">
                  <button
                    onClick={() => setCurrencyFilter('ALL')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${currencyFilter === 'ALL' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setCurrencyFilter('COIN')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${currencyFilter === 'COIN' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    🪙 Coins
                  </button>
                  <button
                    onClick={() => setCurrencyFilter('DIAMOND')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${currencyFilter === 'DIAMOND' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    💎 Diamonds
                  </button>
                </div>

                {/* Type Filter */}
                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200">
                  {[
                    { key: 'ALL', label: 'All Types' },
                    { key: 'GIFT', label: '🎁 Gifts' },
                    { key: 'RECHARGE', label: '💳 Recharges' },
                    { key: 'REWARD', label: '🏆 Rewards' },
                    { key: 'DEDUCT', label: '🔻 Deduct' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setTypeFilter(tab.key)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${typeFilter === tab.key ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="w-full lg:w-48">
              <FilterCountrySelect
                value={countryFilter}
                onChange={setCountryFilter}
                placeholder="All Countries"
              />
            </div>

            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input 
                type="text" 
                placeholder={activeTab === 'wallets' ? "Search Name, XOXO ID, Email..." : "Search Ledger, User, Reason, TXN ID..."} 
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition placeholder-slate-400" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Content Table */}
        {loading ? (
          <div className="p-24 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600 font-bold text-sm">Retrieving Live Financial Ledger...</p>
          </div>
        ) : activeTab === 'wallets' ? (
          
          /* ======================================================== */
          /* 1. USER WALLETS VIEW WITH DIRECT HISTORY BUTTON          */
          /* ======================================================== */
          filteredWallets.length === 0 ? (
            <div className="p-24 text-center text-slate-400">
              <Wallet size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-bold">No user wallets found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 text-slate-400 text-xs font-black uppercase tracking-wider border-b border-slate-100">
                    <th className="p-5">User Details</th>
                    <th className="p-5">XOXO ID & Email</th>
                    <th className="p-5">Role</th>
                    <th className="p-5">Location</th>
                    <th className="p-5">Coin Balance 🪙</th>
                    <th className="p-5">Diamond Balance 💎</th>
                    <th className="p-5">Last Activity</th>
                    <th className="p-5 text-right">Actions & Audit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredWallets.map((w) => (
                    <tr key={w.user_id} className="hover:bg-slate-50/70 transition">
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          <img 
                            src={w.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(w.user_name || 'User')}&background=fce7f3&color=db2777`}
                            alt="Avatar"
                            className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
                          />
                          <div>
                            <p className="font-black text-slate-800">{w.user_name || 'Unnamed User'}</p>
                            <p className="text-xs text-slate-400 font-medium">{w.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-5">
                        <span className="px-2.5 py-0.5 bg-pink-50 text-pink-700 font-extrabold text-xs rounded-md border border-pink-100">
                          XOXO ID: {w.xoxo_id || 'N/A'}
                        </span>
                      </td>
                      <td className="p-5">
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-700 font-black text-[11px] rounded-lg border border-slate-200 uppercase">
                          {w.user_type || 'USER'}
                        </span>
                      </td>
                      <td className="p-5">
                        <CountryBadge country={w.country} />
                      </td>
                      <td className="p-5">
                        <div className="font-black text-slate-900 text-base flex items-center gap-1.5">
                          🪙 {Number(w.balance || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="flex flex-col">
                          <div className="font-black text-purple-700 text-base flex items-center gap-1.5">
                            💎 {Number(w.diamond_balance || 0).toLocaleString()}
                          </div>
                          {Number(w.total_pending_diamonds || 0) > 0 && (
                            <div className="inline-flex items-center gap-1 text-[10px] font-extrabold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md mt-1 border border-amber-200 w-fit" title="Locked awaiting 10 streaming days">
                              <Clock size={11} className="text-amber-600 shrink-0" />
                              <span>⏳ 💎 {Number(w.total_pending_diamonds).toLocaleString()} Pending</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-5 text-slate-500 text-xs font-semibold">
                        {w.lastmodified_at ? new Date(w.lastmodified_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="p-5 text-right">
                        <div className="flex justify-end items-center gap-2">
                          
                          {/* 📜 Direct History Button */}
                          <button 
                            onClick={() => handleViewUserHistory(w)}
                            className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 px-3.5 py-1.5 rounded-xl text-xs font-black hover:bg-indigo-100 active:scale-95 transition shadow-sm"
                            title="View Complete Statement & History for this user"
                          >
                            <History size={14} /> History
                          </button>

                          <button 
                            onClick={() => handleOpenAdjustModal(w, 'ADD')}
                            className="flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-xl text-xs font-black hover:bg-green-100 active:scale-95 transition shadow-sm"
                          >
                            <PlusCircle size={13} /> Add
                          </button>
                          <button 
                            onClick={() => handleOpenAdjustModal(w, 'DEDUCT')}
                            className="flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-xl text-xs font-black hover:bg-rose-100 active:scale-95 transition shadow-sm"
                          >
                            <MinusCircle size={13} /> Deduct
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : activeTab === 'ledger' ? (
          
          /* ======================================================== */
          /* 2. FULL FINANCIAL AUDIT LEDGER (TRANSACTIONS)            */
          /* ======================================================== */
          filteredTransactions.length === 0 ? (
            <div className="p-24 text-center text-slate-400">
              <Clock size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-bold">No transaction records found matching filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 text-slate-400 text-xs font-black uppercase tracking-wider border-b border-slate-100">
                    <th className="p-5">Txn ID & Timestamp</th>
                    <th className="p-5">User</th>
                    <th className="p-5">Type</th>
                    <th className="p-5">Amount (Delta)</th>
                    <th className="p-5">Balance Audit (Before → After)</th>
                    <th className="p-5">Complete Description / Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {filteredTransactions.map((tx) => {
                    const txType = (tx.transaction_type || '').toUpperCase();
                    const isDiamond = (tx.currency_type || '').toUpperCase() === 'DIAMOND';
                    
                    const isCredit = txType === 'COIN_ADD' || 
                                    txType === 'SELF_RECHARGE' || 
                                    txType === 'FRIEND_RECHARGE' || 
                                    txType === 'ROOM_BONUS_CLAIM' || 
                                    txType === 'TARGET_REWARD' ||
                                    (tx.balance_after !== undefined && tx.balance_before !== undefined && Number(tx.balance_after) > Number(tx.balance_before));
                    
                    const isGift = txType.includes('GIFT');
                    const isRecharge = txType.includes('RECHARGE') || txType === 'COIN_ADD';
                    const isReward = txType.includes('REWARD') || txType.includes('TARGET');
                    const isDeduct = txType.includes('DEDUCT');

                    return (
                      <tr key={tx.id} className="hover:bg-slate-50/70 transition">
                        
                        {/* Transaction ID & Time */}
                        <td className="p-5">
                          <p className="font-black text-slate-900 tracking-wide text-xs">
                            TXN-{String(tx.id).substring(0, 8).toUpperCase()}
                          </p>
                          <p className="text-[11px] text-slate-500 font-semibold mt-0.5 flex items-center gap-1">
                            <Clock size={11} className="text-slate-400" />
                            {new Date(tx.created_at || tx.timestamp).toLocaleString()}
                          </p>
                        </td>

                        {/* User Details */}
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <img 
                              src={tx.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(tx.user_name || 'User')}&background=f1f5f9&color=475569`}
                              alt="Avatar"
                              className="w-9 h-9 rounded-full object-cover border border-slate-200"
                            />
                            <div>
                              <p className="font-black text-slate-800 text-sm leading-tight">
                                {tx.user_name || 'Anonymous User'}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <button
                                  onClick={() => setSelectedUserFilter(tx.xoxo_id || '')}
                                  className="text-[10px] font-extrabold text-pink-600 bg-pink-50 hover:bg-pink-100 px-1.5 py-0.5 rounded border border-pink-100 transition"
                                  title="Click to filter ledger by this user"
                                >
                                  XOXO ID: {tx.xoxo_id || 'N/A'}
                                </button>
                                {tx.email && <span className="text-[10px] text-slate-400 font-medium truncate max-w-[130px]">{tx.email}</span>}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Transaction Type Badge */}
                        <td className="p-5">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wide border ${
                            isReward ? 'bg-purple-50 text-purple-700 border-purple-200' :
                            isGift ? 'bg-rose-50 text-rose-700 border-rose-200' :
                            isRecharge ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            isDeduct ? 'bg-orange-50 text-orange-700 border-orange-200' :
                            'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            {isReward && <Award size={13} />}
                            {isGift && <Gift size={13} />}
                            {isRecharge && <CreditCard size={13} />}
                            {isDeduct && <ArrowUpRight size={13} />}
                            {tx.transaction_type}
                          </span>
                        </td>

                        {/* Amount Delta */}
                        <td className="p-5">
                          <div className={`font-black text-base flex items-center gap-1 ${
                            isCredit ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {isCredit ? '+' : '-'}{Math.abs(Number(tx.amount || 0)).toLocaleString()}
                            <span className="text-xs font-bold">{isDiamond ? '💎' : '🪙'}</span>
                          </div>
                        </td>

                        {/* Balance Path (Before -> After) */}
                        <td className="p-5">
                          <div className="inline-flex items-center gap-2 bg-slate-100/80 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 border border-slate-200/60">
                            <span>{isDiamond ? '💎' : '🪙'} {Number(tx.balance_before || 0).toLocaleString()}</span>
                            <ArrowRight size={12} className="text-slate-400" />
                            <span className="text-slate-900 font-black">{isDiamond ? '💎' : '🪙'} {Number(tx.balance_after || 0).toLocaleString()}</span>
                          </div>
                        </td>

                        {/* Description & Reason */}
                        <td className="p-5">
                          <p className="text-xs font-bold text-slate-800">
                            {tx.reason || 'Standard system transfer'}
                          </p>
                          {tx.reference_id && (
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                              Ref: {tx.reference_id}
                            </p>
                          )}
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : activeTab === 'slabs' ? (

          /* ======================================================== */
          /* 3. DIAMOND TO COIN EXCHANGE SLABS CONFIGURATION         */
          /* ======================================================== */
          <div className="p-8 space-y-8 animate-in fade-in duration-200">
            
            {/* Header info banner */}
            <div className="bg-gradient-to-r from-purple-900/90 via-indigo-900/90 to-slate-900 p-6 rounded-3xl text-white border border-purple-500/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-lg shadow-purple-900/10">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/20 text-purple-300 rounded-2xl border border-purple-400/30">
                  <Sparkles size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black">Diamond to Coin Exchange Slabs (Diamonds 💎 ➔ Coins 🪙)</h3>
                  <p className="text-purple-200/80 text-xs mt-0.5">
                    Configure the 6 preset conversion slabs and custom exchange conversion ratio for mobile users.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {slabSaveSuccess && (
                  <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-3.5 py-2 rounded-xl text-xs font-black animate-in fade-in">
                    <Check size={14} /> Slabs Saved & Synced!
                  </div>
                )}
                <button
                  onClick={handleSaveSlabs}
                  disabled={savingSlabs}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-black rounded-2xl transition active:scale-95 shadow-lg shadow-purple-500/30 text-xs disabled:opacity-50"
                >
                  <RefreshCw size={14} className={savingSlabs ? 'animate-spin' : ''} />
                  {savingSlabs ? 'Saving Slabs...' : 'Save & Publish Slabs'}
                </button>
              </div>
            </div>

            {/* Custom ratio setting card */}
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-black text-slate-900">Custom Exchange Ratio (For custom diamond amounts)</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  When a user types a custom diamond amount (e.g. 10,000 💎), how many coins per diamond do they get?
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl border border-slate-200 shadow-sm">
                <span className="text-xs font-bold text-slate-600">1 💎 =</span>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10.0"
                  value={customRatio}
                  onChange={(e) => setCustomRatio(parseFloat(e.target.value) || 1.0)}
                  className="w-16 text-center font-black text-sm bg-slate-100 rounded-lg py-1 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-xs font-bold text-slate-600">🪙 Coins</span>
              </div>
            </div>

            {/* 6 Slabs Grid */}
            <div>
              <h4 className="text-sm font-black text-slate-900 mb-4 flex items-center gap-2">
                <span>Preset Exchange Slabs ({exchangeSlabs.length} Slabs)</span>
                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                  Mobile App Diamonds Tab
                </span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {exchangeSlabs.map((slab, index) => {
                  const d = Number(slab.diamond_amount || 0);
                  const c = Number(slab.coin_amount || 0);
                  const mult = d > 0 ? (c / d).toFixed(1) : '2.0';

                  return (
                    <div 
                      key={slab.id || index}
                      className={`p-6 rounded-3xl border transition relative overflow-hidden bg-white shadow-sm hover:shadow-md ${
                        slab.is_active ? 'border-purple-200' : 'border-slate-200 opacity-60 bg-slate-50'
                      }`}
                    >
                      {/* Top badge & active toggle */}
                      <div className="flex justify-between items-center mb-4">
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 font-black text-xs rounded-full border border-purple-100">
                          Slab #{index + 1}
                        </span>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={slab.is_active !== false}
                            onChange={(e) => handleSlabChange(index, 'is_active', e.target.checked)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-xs font-bold text-slate-600">
                            {slab.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>

                      {/* Diamond input */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                            <span>Diamonds Required (💎)</span>
                            {d > 0 && (
                              <span className="text-[10px] font-black text-purple-600">
                                💎 {d.toLocaleString('en-IN')}
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={d > 0 ? d.toLocaleString('en-IN') : ''}
                            onChange={(e) => handleSlabChange(index, 'diamond_amount', e.target.value)}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="e.g. 10,000"
                          />
                        </div>

                        {/* Coin reward input */}
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                            <span>Coins Given (🪙)</span>
                            {c > 0 && (
                              <span className="text-[10px] font-black text-amber-600">
                                🪙 {c.toLocaleString('en-IN')}
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={c > 0 ? c.toLocaleString('en-IN') : ''}
                            onChange={(e) => handleSlabChange(index, 'coin_amount', e.target.value)}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="e.g. 20,000"
                          />
                        </div>
                      </div>

                      {/* Multiplier / Rate Preview */}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-semibold">Conversion Rate:</span>
                        <span className="px-2.5 py-1 bg-amber-50 text-amber-800 font-black rounded-lg border border-amber-200">
                          {mult}x Multiplier (1 💎 = {mult} 🪙)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom Save bar */}
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                onClick={handleSaveSlabs}
                disabled={savingSlabs}
                className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-black rounded-2xl transition active:scale-95 shadow-xl shadow-purple-600/30 text-sm disabled:opacity-50"
              >
                <Check size={16} />
                {savingSlabs ? 'Saving & Publishing...' : 'Save All 6 Slabs Now'}
              </button>
            </div>

          </div>
        ) : (
          /* 4. USER COIN PURCHASE SLABS (INR -> Coins) */
          <div className="p-8 space-y-8 animate-in fade-in duration-200">
            <div className="bg-gradient-to-r from-emerald-900/90 via-teal-900/90 to-slate-900 p-6 rounded-3xl text-white border border-emerald-500/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-lg shadow-emerald-900/10">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-500/20 text-emerald-300 rounded-2xl border border-emerald-400/30">
                  <Coins size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black">User Coin Purchase Slabs (₹ INR ➔ Coins 🪙)</h3>
                  <p className="text-emerald-200/80 text-xs mt-0.5">
                    Configure the coin packages available for users to purchase directly in the mobile app.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {coinSlabSaveSuccess && (
                  <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-3.5 py-2 rounded-xl text-xs font-black animate-in fade-in">
                    <Check size={14} /> Saved!
                  </div>
                )}
                <button
                  onClick={handleSaveCoinSlabs}
                  disabled={savingCoinSlabs}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-black rounded-2xl transition active:scale-95 shadow-lg shadow-emerald-500/30 text-xs disabled:opacity-50"
                >
                  <RefreshCw size={14} className={savingCoinSlabs ? 'animate-spin' : ''} />
                  {savingCoinSlabs ? 'Saving...' : 'Save Coin Slabs'}
                </button>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <span>User Coin Packages ({coinSlabs.length} Slabs)</span>
                </h4>
                <button
                  onClick={addNewCoinSlab}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg text-xs font-bold transition border border-emerald-200"
                >
                  <PlusCircle size={14} /> Add Package
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coinSlabs.map((slab, index) => {
                  const c = Number(slab.coin_amount || 0);
                  const p = Number(slab.price_inr || 0);
                  const u = slab.price_usd !== undefined && slab.price_usd !== '' ? Number(slab.price_usd) : Number((p / 94.0).toFixed(2));
                  const rateInr = p > 0 ? (c / p).toFixed(1) : '0.0';
                  const rateUsd = u > 0 ? (c / u).toFixed(0) : '0';

                  return (
                    <div 
                      key={slab.id || index}
                      className={`p-6 rounded-3xl border transition relative overflow-hidden bg-white shadow-sm hover:shadow-md ${
                        slab.is_active ? 'border-emerald-200' : 'border-slate-200 opacity-60 bg-slate-50'
                      }`}
                    >
                      {/* Top badge & actions */}
                      <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-black">
                            {index + 1}
                          </span>
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                            Package
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={slab.is_active !== false}
                              onChange={(e) => handleCoinSlabChange(index, 'is_active', e.target.checked)}
                              className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                            />
                            <span className="text-xs font-bold text-slate-600">
                              {slab.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                          <button
                            onClick={() => removeCoinSlab(index)}
                            className="text-rose-400 hover:text-rose-600 transition"
                            title="Remove Package"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Coins */}
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                            <span>Coins To Give (🪙)</span>
                            {c > 0 && <span className="text-[10px] font-black text-amber-600">🪙 {c.toLocaleString('en-IN')}</span>}
                          </label>
                          <input
                            type="text"
                            value={c > 0 ? c.toLocaleString('en-IN') : ''}
                            onChange={(e) => handleCoinSlabChange(index, 'coin_amount', e.target.value)}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="e.g. 1,000"
                          />
                        </div>

                        {/* Dual Currency Prices Grid */}
                        <div className="grid grid-cols-2 gap-2">
                          {/* Price INR */}
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                              <span>Price (₹ INR)</span>
                            </label>
                            <input
                              type="text"
                              value={p > 0 ? p : ''}
                              onChange={(e) => handleCoinSlabChange(index, 'price_inr', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              placeholder="e.g. 96"
                            />
                          </div>

                          {/* Price USD */}
                          <div>
                            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                              <span>Price ($ USD)</span>
                            </label>
                            <input
                              type="text"
                              value={u > 0 ? u : ''}
                              onChange={(e) => handleCoinSlabChange(index, 'price_usd', e.target.value)}
                              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g. 1.00"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-semibold">Value Rate:</span>
                        <div className="flex gap-1.5">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 font-black rounded-md border border-emerald-200 text-[11px]">
                            {rateInr} 🪙/₹
                          </span>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-800 font-black rounded-md border border-blue-200 text-[11px]">
                            {rateUsd} 🪙/$
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* 3. USER INDIVIDUAL STATEMENT / HISTORY MODAL             */}
      {/* ======================================================== */}
      {historyModalUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-900 to-indigo-950 text-white shrink-0">
              <div className="flex items-center gap-3">
                <img 
                  src={historyModalUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(historyModalUser.user_name || 'User')}`}
                  alt="Avatar"
                  className="w-12 h-12 rounded-full object-cover border-2 border-pink-500"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black">{historyModalUser.user_name || 'User'}</h3>
                    <span className="px-2 py-0.5 bg-pink-500 text-white font-extrabold text-[10px] rounded-full">
                      ID: {historyModalUser.xoxo_id || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    {historyModalUser.email} • Current: 🪙 {Number(historyModalUser.balance || 0).toLocaleString()} Coins | 💎 {Number(historyModalUser.diamond_balance || 0).toLocaleString()} Diamonds
                    {Number(historyModalUser.total_pending_diamonds || 0) > 0 && (
                      <span className="text-amber-300 font-bold ml-1">
                        | ⏳ 💎 {Number(historyModalUser.total_pending_diamonds).toLocaleString()} Pending (10 Days Required)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFilterLedgerByUser(historyModalUser.xoxo_id || historyModalUser.user_id)}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition flex items-center gap-1"
                >
                  <FileText size={13} /> Full Ledger View
                </button>
                <button 
                  onClick={() => setHistoryModalUser(null)} 
                  className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition"
                >
                  <XCircle size={22} />
                </button>
              </div>
            </div>

            {/* Modal Body: User's Statement Table */}
            <div className="flex-1 overflow-y-auto p-6">
              {modalLoading ? (
                <div className="p-16 text-center text-slate-400">
                  <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="font-bold text-slate-600">Loading complete transaction statement...</p>
                  <p className="text-xs text-slate-400 mt-1">Fetching live financial records from the database...</p>
                </div>
              ) : userModalTransactions.length === 0 ? (
                <div className="p-16 text-center text-slate-400">
                  <Clock size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-bold text-slate-600">No transaction records found for this user.</p>
                  <p className="text-xs mt-1">This user has not made any recharges, gifts, or adjustments yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                    Complete Audit Statement ({userModalTransactions.length} Records)
                  </p>
                  {userModalTransactions.map((tx) => {
                    const txType = (tx.transaction_type || '').toUpperCase();
                    const isDiamond = (tx.currency_type || '').toUpperCase() === 'DIAMOND';
                    const isCredit = txType === 'COIN_ADD' || 
                                    txType === 'SELF_RECHARGE' || 
                                    txType === 'FRIEND_RECHARGE' || 
                                    txType === 'ROOM_BONUS_CLAIM' || 
                                    txType === 'TARGET_REWARD' ||
                                    (tx.balance_after !== undefined && tx.balance_before !== undefined && Number(tx.balance_after) > Number(tx.balance_before));
                    
                    return (
                      <div key={tx.id} className="p-4 bg-slate-50/80 rounded-2xl border border-slate-100 flex items-center justify-between hover:bg-slate-100/60 transition">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${
                            isCredit ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                          }`}>
                            {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-black text-slate-800 text-xs uppercase">{tx.transaction_type}</span>
                              <span className="text-[10px] text-slate-400 font-mono">TXN-{String(tx.id).substring(0, 8)}</span>
                            </div>
                            <p className="text-xs text-slate-600 font-medium mt-0.5">{tx.reason || 'Transaction'}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{new Date(tx.created_at || tx.timestamp).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className={`font-black text-sm ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isCredit ? '+' : '-'}{Math.abs(Number(tx.amount || 0)).toLocaleString()} {isDiamond ? '💎' : '🪙'}
                          </div>
                          <div className="text-[10px] font-semibold text-slate-500 mt-0.5">
                            {isDiamond ? '💎' : '🪙'} {Number(tx.balance_before || 0).toLocaleString()} → {isDiamond ? '💎' : '🪙'} {Number(tx.balance_after || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
              <button
                onClick={() => setHistoryModalUser(null)}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition"
              >
                Close Statement
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 4. ADJUST BALANCE MODAL                                  */}
      {/* ======================================================== */}
      {isAdjustModalOpen && selectedWallet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className={`p-2.5 rounded-2xl ${adjustType === 'ADD' ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}>
                  {adjustType === 'ADD' ? <PlusCircle size={20} /> : <MinusCircle size={20} />}
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">
                    {adjustType === 'ADD' 
                      ? `Issue / Add ${adjustCurrency === 'DIAMOND' ? 'Diamonds 💎' : 'Coins 🪙'}` 
                      : `Deduct / Revoke ${adjustCurrency === 'DIAMOND' ? 'Diamonds 💎' : 'Coins 🪙'}`}
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">Manual Administrator Wallet Adjustment</p>
                </div>
              </div>
              <button 
                onClick={() => setIsAdjustModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-200/60 transition"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleAdjustBalance} className="p-6 space-y-4">
              
              {/* Currency Selector (Coins / Diamonds) */}
              <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-2xl">
                <button
                  type="button"
                  onClick={() => setAdjustCurrency('COIN')}
                  className={`py-2 px-3 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 transition ${
                    adjustCurrency === 'COIN' 
                      ? 'bg-white text-yellow-700 shadow-sm border border-slate-200 scale-[1.02]' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <span>🪙 Coins Balance</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustCurrency('DIAMOND')}
                  className={`py-2 px-3 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 transition ${
                    adjustCurrency === 'DIAMOND' 
                      ? 'bg-white text-pink-600 shadow-sm border border-slate-200 scale-[1.02]' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <span>💎 Diamonds Balance</span>
                </button>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center gap-3">
                <img 
                  src={selectedWallet.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedWallet.user_name || 'User')}`}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full object-cover border border-slate-200"
                />
                <div>
                  <p className="font-black text-slate-800 text-sm">{selectedWallet.user_name || 'User'}</p>
                  <p className="text-xs text-slate-500 font-semibold flex items-center gap-2 mt-0.5">
                    <span>🪙 {Number(selectedWallet.balance || 0).toLocaleString()} Coins</span>
                    <span>•</span>
                    <span className="text-pink-600 font-bold">💎 {Number(selectedWallet.diamond_balance || 0).toLocaleString()} Diamonds</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black uppercase text-slate-600 mb-1.5 tracking-wide flex justify-between items-center">
                  <span>{adjustCurrency === 'DIAMOND' ? 'Diamonds' : 'Coins'} Amount to {adjustType === 'ADD' ? 'Credit' : 'Debit'}</span>
                  {getRawNumber(adjustAmount) > 0 && (
                    <span className="text-[11px] font-black text-pink-600 bg-pink-50 px-2 py-0.5 rounded-md border border-pink-200">
                      {adjustCurrency === 'DIAMOND' ? '💎' : '🪙'} {formatAmountInWords(getRawNumber(adjustAmount))}
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    required
                    placeholder={adjustCurrency === 'DIAMOND' ? "e.g. 5,000" : "e.g. 20,000"} 
                    value={adjustAmount}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                      if (!raw) {
                        setAdjustAmount('');
                      } else {
                        setAdjustAmount(Number(raw).toLocaleString('en-IN'));
                      }
                    }}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-base font-black text-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition tracking-wide"
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm pointer-events-none">
                    {adjustCurrency === 'DIAMOND' ? '💎' : '🪙'}
                  </div>
                </div>

                {/* Quick preset chips */}
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {[1000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setAdjustAmount(preset.toLocaleString('en-IN'))}
                      className="px-2.5 py-1 bg-slate-100 hover:bg-pink-50 hover:text-pink-600 hover:border-pink-200 border border-slate-200 rounded-lg text-[10px] font-black text-slate-600 transition active:scale-95"
                    >
                      +{preset.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>

                {/* Live Balance Impact Preview */}
                {getRawNumber(adjustAmount) > 0 && (
                  <div className="mt-3 p-3 bg-slate-100 rounded-xl border border-slate-200 flex items-center justify-between text-xs font-bold">
                    <span className="text-slate-500">Calculated New {adjustCurrency === 'DIAMOND' ? 'Diamond' : 'Coin'} Balance:</span>
                    <span className="text-slate-800 font-black">
                      {adjustCurrency === 'DIAMOND' ? '💎' : '🪙'} {Number(adjustCurrency === 'DIAMOND' ? (selectedWallet.diamond_balance || 0) : (selectedWallet.balance || 0)).toLocaleString('en-IN')} {adjustType === 'ADD' ? '+' : '-'} {getRawNumber(adjustAmount).toLocaleString('en-IN')} ➔ {adjustCurrency === 'DIAMOND' ? '💎' : '🪙'} {(adjustType === 'ADD' ? (Number(adjustCurrency === 'DIAMOND' ? (selectedWallet.diamond_balance || 0) : (selectedWallet.balance || 0)) + getRawNumber(adjustAmount)) : Math.max(0, Number(adjustCurrency === 'DIAMOND' ? (selectedWallet.diamond_balance || 0) : (selectedWallet.balance || 0)) - getRawNumber(adjustAmount))).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-black uppercase text-slate-600 mb-1.5 tracking-wide">
                  Reason / Audit Note
                </label>
                <textarea 
                  rows="3"
                  placeholder="e.g. Compensation for payment failure, manual VIP test recharge, host reward adjustment..."
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                ></textarea>
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setIsAdjustModalOpen(false)}
                  className="w-1/2 py-3 bg-slate-100 text-slate-700 font-bold rounded-2xl hover:bg-slate-200 transition text-xs"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={adjustLoading}
                  className={`w-1/2 py-3 text-white font-black rounded-2xl shadow-lg transition text-xs flex items-center justify-center gap-1.5 ${
                    adjustType === 'ADD' ? 'bg-green-600 hover:bg-green-700 shadow-green-600/25' : 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/25'
                  }`}
                >
                  {adjustLoading ? 'Processing...' : adjustType === 'ADD' ? `Confirm Add ${adjustCurrency === 'DIAMOND' ? 'Diamonds' : 'Coins'}` : `Confirm Deduct ${adjustCurrency === 'DIAMOND' ? 'Diamonds' : 'Coins'}`}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default WalletManagement;